import { createFileRoute, Outlet, redirect, useNavigate, useRouterState } from '@tanstack/react-router';
import * as v from 'valibot';
import { useCallback, useLayoutEffect, useRef } from 'react';
import { useLingui } from '@lingui/react/macro';
import { ChevronRightIcon } from 'lucide-react';
import { Button } from '@worldcoo/ui/components/button';
import { Progress } from '@worldcoo/ui/components/progress';
import { WorldcooLogo } from '@worldcoo/ui/custom/worldcoo-logo';
import { TOTAL_ONBOARDING_STEPS, useOnboardingFormStore } from '@/lib/onboarding-form-store';
import { navigateWithViewTransition } from '@/lib/view-transition-utils';

export const Route = createFileRoute('/ngos/onboarding')({
	validateSearch: v.object({
		new: v.fallback(v.optional(v.string()), undefined),
	}),
	beforeLoad: ({ search }) => {
		if (search.new !== 'true') {
			throw redirect({ href: 'https://backoffice.worldcoo.com/onboarding' });
		}
	},
	component: RouteComponent,
});

/**
 * Derives current step from router matches (not location) so that the step bar
 * updates at the same time as the <Outlet /> content, avoiding a visible desync.
 */
function useCurrentStep(): number {
	const lastRouteId = useRouterState({
		select: (s) => s.matches[s.matches.length - 1]?.routeId ?? '',
	});
	if (lastRouteId === '/ngos/onboarding/success') return 6;
	if (lastRouteId === '/ngos/onboarding/terms') return 5;
	if (lastRouteId === '/ngos/onboarding/privacy') return 4;
	if (lastRouteId === '/ngos/onboarding/accreditation') return 3;
	if (lastRouteId === '/ngos/onboarding/contacts') return 2;
	return 1;
}

function RouteComponent() {
	const { t } = useLingui();
	const navigate = useNavigate();
	const currentStep = useCurrentStep();

	const stepLabels = [
		t`Organization Details`,
		t`Contacts`,
		t`Accreditation & Donations`,
		t`Data Privacy`,
		t`Terms & Conditions`,
		// t`Contract`,
	];

	const scrollRef = useRef<HTMLDivElement>(null);
	const _submitted = useOnboardingFormStore((s) => s.submitted);

	const navigateToLogin = useCallback(
		(e: React.MouseEvent) => {
			e.preventDefault();
			navigateWithViewTransition(
				() => navigate({ to: '/ngos/login' }),
				() => !!document.querySelector('[data-page="login"]'),
			);
		},
		[navigate],
	);

	useLayoutEffect(() => {
		if (scrollRef.current) scrollRef.current.scrollTop = 0;
	}, [currentStep]);

	return (
		<div
			data-page="onboarding"
			className="flex h-screen bg-[#fff7ed]"
			style={{ '--primary': '#f28e41' } as React.CSSProperties}
		>
			<div className="mx-auto flex min-h-0 w-full max-w-[1280px] flex-col p-3">
				{/* White card */}
				<main
					className="border-portal-border flex min-h-0 flex-1 flex-col overflow-hidden rounded-[16px] border bg-white"
					style={{ viewTransitionName: 'ngo-main-content' }}
				>
					<PageHeader onNavigateToLogin={navigateToLogin} />

					{/* Scrollable area: progress + form */}
					<div ref={scrollRef} className="min-h-0 flex-1 overflow-y-scroll">
						{/* Progress indicator */}
						<div className="px-8 pt-8">
							<div className="mx-auto w-full max-w-[603px]">
								<span className="text-muted-foreground text-sm">{t`Step ${currentStep} of ${TOTAL_ONBOARDING_STEPS}`}</span>
								<Progress value={(currentStep / TOTAL_ONBOARDING_STEPS) * 100} className="mt-1 h-2.5" />
								<p className="mt-1 text-base font-medium">{stepLabels[currentStep - 1]}</p>
							</div>
						</div>

						{/* Step content */}
						<div className="px-6 py-6">
							<div className="mx-auto max-w-[603px]">
								<Outlet />
							</div>
						</div>
					</div>

					{/* Bottom action bar */}
					{currentStep < TOTAL_ONBOARDING_STEPS && (
						<StepNavigation currentStep={currentStep} onNavigateToLogin={navigateToLogin} />
					)}
				</main>
			</div>
		</div>
	);
}

function StepNavigation(props: { currentStep: number; onNavigateToLogin: (e: React.MouseEvent) => void }) {
	const { t } = useLingui();

	return (
		<div className="border-portal-border flex items-center justify-center border-t bg-white px-6 py-6">
			<div className="flex w-[408px] gap-2">
				{/* Next/Submit rendered first in DOM so it's the default submit button for Enter key */}
				{props.currentStep < TOTAL_ONBOARDING_STEPS - 1 ? (
					<Button size="xl" className="order-1 flex-1" type="submit" form="onboarding-step-form">
						{t`Next`}
					</Button>
				) : (
					<Button size="xl" className="order-1 flex-1" type="submit" form="onboarding-step-form">
						{t`Submit`}
					</Button>
				)}
				{props.currentStep > 1 ? (
					<Button
						variant="outline-dark"
						size="xl"
						className="flex-1"
						form="onboarding-step-form"
						name="action"
						value="back"
					>
						{t`Previous`}
					</Button>
				) : (
					<Button variant="outline-dark" size="xl" className="flex-1" onClick={props.onNavigateToLogin}>
						{t`Cancel`}
					</Button>
				)}
			</div>
		</div>
	);
}

function PageHeader(props: { onNavigateToLogin: (e: React.MouseEvent) => void }) {
	const { t } = useLingui();
	return (
		<div className="border-portal-border border-b p-6">
			<div className="flex items-center gap-2">
				<button type="button" onClick={props.onNavigateToLogin}>
					<WorldcooLogo accentClassName="text-[#f28e41]" className="h-8 w-auto" />
				</button>
				<ChevronRightIcon className="text-muted-foreground size-3" />
				<span className="text-primary text-sm">{t`Organization Onboarding`}</span>
			</div>
			<h1 className="mt-2 text-[28px] font-medium tracking-[-0.2px]">{t`Organization Onboarding`}</h1>
		</div>
	);
}
